import React, { FC } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
// redux
import { useAppSelector } from "_redux/hooks";
import {
  orderApprovalsSelector,
  selectGroupedBasketsBySeller,
  selectSellerValidations,
} from "_redux/selectors/order";
// components
import HeaderLogo from "components/header/HeaderLogo";
import { StyledImage, StyledProtectable } from "components/StyledUI";
import {
  Basket,
  SellerType,
} from "@efood-commerce-sdk/typescript-axios-transaction-efood";
import {
  getDeliveryDateDate,
  fromResponseDeliveryDate,
} from "tools/delivery-date-helper";
import { tss } from "tss-react/mui";
import { isEqual } from "lodash-es";
import { useTranslation } from "react-i18next";
import {
  SaveInListButton,
  ClearCartButton,
  OrderSellerNowButton,
} from "../CartButtons";
import CartFreeProducts from "../CartFreeProduct/CartFreeProducts";
import TermsHint from "./TermsHint";
import OrderNotifications from "./OrderNotifications";
import SellerGroupedNotifications from "./SellerGroupedNotifications";
import { createBasketKey } from "./CartBaskets.helper";
import CartSellerGroupedPriceSummary from "./CartSellerGroupedPriceSummary";
import OrderComment from "./OrderComment";
import MarketplaceCartSubBaskets from "./MarketplaceCartSubBaskets";

const useStyles = tss.create(({ theme }) => ({
  root: {
    border: `2px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    width: "100%",
    marginTop: theme.spacing(2),
  },
  container1: {
    display: "grid",
    alignItems: "center",
    width: "100%",
    gridTemplateColumns: "180px auto 0px 52px 52px 395px",
  },
  displayNameAndIcons: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 1,
  },
  item1: {
    "& img": {
      width: "180px",
      height: "auto",
    },
  },
  item3: {
    gridColumn: "4",
    textAlign: "right",
  },
  item4: {
    gridColumn: "5",
    textAlign: "right",
  },
  item5: {
    paddingLeft: theme.spacing(1),
    paddingTop: theme.spacing(2),
    gridColumn: "6",
    textAlign: "right",
  },
}));

interface Props {
  orderId: string;
}

const MarketplaceCartBaskets: FC<Props> = ({ orderId }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const breakpointDownXs = useMediaQuery(theme.breakpoints.down("sm"));
  const breakpointUpLg = useMediaQuery(theme.breakpoints.up("lg"));

  const approvals = useAppSelector(orderApprovalsSelector, isEqual);
  const sellers = useAppSelector(selectGroupedBasketsBySeller, isEqual);
  const sellerValidations = useAppSelector(selectSellerValidations, isEqual);

  return (
    <Grid container xs={12} className="grid-item">
      {sellers.map((seller) => {
        const logo =
          seller.sellerType === SellerType.Internal ? (
            <HeaderLogo />
          ) : (
            <StyledImage
              imageSrc={seller.image1}
              imageAlt={`${seller.displayName} Logo`}
              hoverText={seller.displayName}
            />
          );

        const validations = sellerValidations[seller.identifier] ?? [];
        const hasDenialValidation = validations.some(
          (val) => val.validation.type === "DENIAL"
        );

        return (
          <Box key={seller.identifier} className={classes.root}>
            <SellerGroupedNotifications validations={validations} />

            {breakpointUpLg ? (
              <Box mb={1}>
                <div className={classes.container1}>
                  <div className={classes.item1}>{logo}</div>
                  <div />
                  <div className={classes.item3}>
                    <SaveInListButton iconOnly />
                  </div>
                  <div className={classes.item4}>
                    <ClearCartButton iconOnly variant="contained" />
                  </div>
                  <div className={classes.item5}>
                    <OrderSellerNowButton
                      disabled={hasDenialValidation}
                      identifier={seller.identifier}
                    />
                  </div>
                </div>
              </Box>
            ) : (
              <Box mb={1}>
                <Box className={classes.displayNameAndIcons}>
                  <Typography variant="h3">{seller.displayName}</Typography>
                  <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                    <SaveInListButton iconOnly />
                    <ClearCartButton iconOnly variant="contained" />
                  </Box>
                </Box>
              </Box>
            )}

            {seller.baskets.map((basket: Basket) => {
              const deliveryDateDate = getDeliveryDateDate(
                fromResponseDeliveryDate(basket.identifier.deliveryDate)
              );

              const approval =
                approvals.find((a) => {
                  if (!deliveryDateDate) return false;
                  const aDate = getDeliveryDateDate(
                    fromResponseDeliveryDate(a.basketIdentifier.deliveryDate)
                  );
                  return (
                    a.basketIdentifier.type === basket.identifier.type &&
                    aDate?.isSame(deliveryDateDate)
                  );
                }) ?? null;

              return (
                <React.Fragment key={createBasketKey(basket.identifier)}>
                  <OrderNotifications approval={approval} />
                  <MarketplaceCartSubBaskets
                    key={createBasketKey(basket.identifier)}
                    orderId={orderId}
                    basket={basket}
                  />
                </React.Fragment>
              );
            })}

            <Grid container spacing={2} alignItems="flex-start">
              <Grid xs={4} md={6} lg={8}>
                {seller.sellerType === SellerType.Internal && (
                  <CartFreeProducts orderId={orderId} />
                )}
                {seller.sellerType === SellerType.External && (
                  // External should have only one basket
                  <OrderComment
                    required={false}
                    text={t("cart.comment")}
                    basket={seller.baskets[0]}
                  />
                )}
              </Grid>
              <Grid xs={8} md={6} lg={4}>
                <StyledProtectable permission="show.price">
                  <CartSellerGroupedPriceSummary
                    total={seller.total}
                    totalProduct={seller.totalProduct}
                    totalAdjustment={seller.totalAdjustment}
                    totalShipping={seller.totalShipping}
                  />
                </StyledProtectable>
              </Grid>
            </Grid>
            <Box mt={2}>
              {seller.sellerType === SellerType.External ? (
                <TermsHint type="seller" />
              ) : (
                <TermsHint />
              )}
              {breakpointDownXs && (
                <Box mt={1}>
                  <OrderSellerNowButton
                    fullWidth
                    identifier={seller.identifier}
                  />
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Grid>
  );
};

export default MarketplaceCartBaskets;
